import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { _onRenderNavLink } from './ZedXMainMenu';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { IEntityDescription1 } from '../types';
import { TruncatedText } from './TruncatedText';
import { decodeRouteId } from '../utils/routeId';

interface ICloeAIMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    entityDescriptionForMenu: IEntityDescription1;
}

type ParentTypes = 'topic' | 'chat';
type ChildView = 'chats' | 'ask-cloe' | 'none';

export const CloeAIMenu = ({
    entityDescriptionForMenu,
    errorHandling: { clearErrors },
    onLinkClicked
}: ICloeAIMenu): React.ReactElement => {

    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();

    const [navs, setNavs] = useState<INavLink[]>([]);
    const resetErrors = useCallback(clearErrors, [clearErrors]);
    const [totalTopics] = useState(0);
    const [totalChats] = useState(0);

    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const hasInitialisedFromRoute = useRef(false);

    // -----------------------------------------
    // parseMenuMode - detect contextual (topic -> chats) mode
    // -----------------------------------------
    const parseMenuMode = (path: string): {
        isContextualMenu: boolean;
        parentType?: ParentTypes;
        parentId?: number | string;
        childView?: ChildView;
    } => {

        // chats route for a topic (child view = chats)
        const chatsForTopicRegex = /^\/a14\/chats-for-topic\/([^/]+)/;
        const matchChatsForTopic = path.match(chatsForTopicRegex);
        if (matchChatsForTopic) {
            const decodedTopicId = decodeRouteId(matchChatsForTopic[1]);
            if (decodedTopicId !== null) {
                return { isContextualMenu: true, parentType: "topic", parentId: decodedTopicId, childView: 'chats' };
            }
        }

        // chat window (ask cloe)
        const askCloeRegex = /^\/a14\/ask-cloe\/([^/]+)/;
        const matchAskCloe = path.match(askCloeRegex);
        if (matchAskCloe) {
            return { isContextualMenu: true, parentType: "chat", parentId: matchAskCloe[1], childView: 'ask-cloe' };
        }

        // default - not contextual
        return { isContextualMenu: false, childView: 'none' };
    };

    const { isContextualMenu, parentType, parentId, childView } = parseMenuMode(pathname);

    /* -------------------------------------------------------
       One-time auto-expand based on initial route (GLOBAL MENU)
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedFromRoute.current || isContextualMenu) return;

        if (pathname.startsWith('/a14/settings')) {
            setSettingsExpanded(true);
        }

        hasInitialisedFromRoute.current = true;
    }, [pathname, isContextualMenu]);

    /* -------------------------------------------------------
       Build navigation
    -------------------------------------------------------- */
    useEffect(() => {
        const routerNav = (path: string): void => {
            resetErrors();
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }: { name: string; path: string }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        let links: INavLink[] = [];

        // -------------------------------------
        // CONTEXTUAL: TOPIC CASE
        // -------------------------------------
        if (isContextualMenu && parentType === "topic" && parentId != null) {

            // --- CHATS MODE
            if (childView === 'chats') {
                links = [
                    { name: 'Home', path: '/' },
                    { name: 'Welcome', path: '/a14/welcome' },
                    { name: 'Chats', path: pathname }
                ].map(NamePathToNavLink);

                setNavs(links);
                return;
            }
        }

        // -------------------------------------
        // CONTEXTUAL: CHAT CASE (Ask Cloe)
        // -------------------------------------
        if (isContextualMenu && parentType === "chat" && parentId != null) {

            if (childView === 'ask-cloe') {
                links = [
                    { name: 'Home', path: '/' },
                    { name: 'Welcome', path: '/a14/welcome' },
                    { name: 'Ask Cloe', path: pathname }
                ].map(NamePathToNavLink);

                setNavs(links);
                return;
            }
        }

        // -------------------------------------
        // GLOBAL MENU
        // -------------------------------------
        links = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a14/welcome' },
            { name: 'Topics', path: '/a14/topics' },
            { name: 'Chats', path: '/a14/chats' }
        ].map(NamePathToNavLink);

        links.push({
            key: 'cloe_settings',
            name: 'Settings',
            url: null,
            isExpanded: settingsExpanded,
            onClick: () => setSettingsExpanded(prev => !prev),
            links: [
                { name: 'Item 1', path: '/a14/settings/item-1' }
            ].map(NamePathToNavLink)
        });

        setNavs(links);

    }, [
        up,
        history,
        onLinkClicked,
        resetErrors,
        pathname,
        settingsExpanded,
        isContextualMenu,
        parentType,
        parentId,
        childView
    ]);

    // determine selectedKey for Nav
    const selectedKey = pathname;

    /* -------------------------------------------------------
       Render
    -------------------------------------------------------- */
    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: 0,
                        paddingBottom: '1px',
                        paddingLeft: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-CloeAI.png`}
                        alt="Cloe AI"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Contextual header info */}
                {isContextualMenu && entityDescriptionForMenu && (
                    <div style={{
                        backgroundColor: '#1A619F',
                        color: 'white',
                        padding: '10px 15px',
                        marginTop: '5px',
                        marginBottom: '10px',
                        marginLeft: '10px',
                        marginRight: '10px',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                    }}>
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                {/* Navigation */}
                <Nav
                    selectedKey={selectedKey}
                    groups={[{ links: navs }]}
                    onRenderLink={(lnk) =>
                        _onRenderNavLink(lnk, totalTopics, totalChats)
                    }
                />
            </div>
        </ThemeProvider>
    );
};